import { IonIcon } from '@ionic/react';
import { pause, play } from 'ionicons/icons';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import * as ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import WaveSurfer from 'wavesurfer.js';
import { on, removeListener } from '../../helper/electron';
import {
  setActiveInstance,
  setPersistentPlayerVisible,
  setPlaying,
  setURL,
} from '../../store/slices/audio-player-state.slice';
import { showUpgradeModal } from '../../store/slices/ui.slice';
import { RootState } from '../../store/store';
import { useToast } from '../ToastManager/useToast';
import './Waveform.scss';

/**************************************
 *  Types
 **************************************/
interface WaveformProps {
  url?: string; // immediate URL (optional)
  loadUrl?: () => Promise<string>; // async URL loader – called only on first play
  height: number;
  changeTrack?: (d: 'next' | 'previous') => void;
  autoplay?: boolean;
  redacted?: boolean;
  isDriveMounted?: boolean;
}

export interface WaveformHandle {
  play: () => void;
  moveToStart: () => void;
  moveToEnd: () => void;
}

/**************************************
 *  Component
 **************************************/
const WaveformInline = forwardRef<WaveformHandle, WaveformProps>(
  (
    {
      url: initialUrl,
      loadUrl,
      height,
      changeTrack,
      autoplay = false,
      redacted = false,
      isDriveMounted = true,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const activeInstance = useSelector(
      (s: RootState) => s.audioPlayerState.activeInstance,
    );
    const globalPlaying = useSelector(
      (s: RootState) => s.audioPlayerState.isPlaying,
    );
    const { errorToast } = useToast();

    /*********************
     *  Local state
     *********************/
    const [url, setUrl] = useState(initialUrl);
    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [previewUnavailable, setPreviewUnavailable] = useState(false);
    const [fileAccessError, setFileAccessError] = useState(false);

    /** Stable instance id for redux */
    const instanceId = useRef(`waveform-${crypto.randomUUID()}`).current;
    const isActive = activeInstance === instanceId;

    /** WaveSurfer refs */
    const wavesurfer = useRef<WaveSurfer | null>(null);
    const waveformEl = useRef<HTMLDivElement | null>(null);

    /** Track visibility */
    const wrapperEl = useRef<HTMLDivElement | null>(null);

    /**************************************
     *  Helpers
     **************************************/
    const toPlayableUrl = (p: string) =>
      /^(file|https?|blob|data):/i.test(p)
        ? p
        : `file://${encodeURI(p.replace(/\\/g, '/').replace(/^([^/])/, '/$1'))}`; // normalize separators and ensure leading slash

    const isProtectedAudio = (p?: string) =>
      typeof p === 'string' && /\.(m4p|m4b)$/i.test(p); // DRM‑protected AAC / audiobooks not previewable

    /**
     * Create OR reuse a single WaveSurfer instance. If Strict‑Mode remounts the
     * component we simply retarget the container instead of destroying.
     */
    const ensureWaveSurfer = () => {
      // if (inlineButtonOnly) return;

      if (wavesurfer.current) {
        if (waveformEl.current)
          wavesurfer.current.setOptions({ container: waveformEl.current });
        return;
      }

      if (!waveformEl.current) return;

      wavesurfer.current = WaveSurfer.create({
        container: waveformEl.current,
        backend: 'MediaElement',
        height,
        waveColor: '#888',
        progressColor: '#3880ff',
        cursorColor: '#3880ff',
        cursorWidth: 2,
        dragToSeek: true,
        normalize: true,
      });

      wavesurfer.current.on('ready', () => {
        setLoading(false);
        setReady(true);
        if (isActive && globalPlaying) wavesurfer.current?.play();
      });

      wavesurfer.current.on('error', (err: any) => {
        const msg = (err?.message || '').toLowerCase();
        if (
          msg.includes('abort') ||
          err?.name === 'aborterror' ||
          msg.includes('signal is aborted')
        )
          return; // benign

        // Check if this is a file access error (file was deleted or inaccessible)
        if (
          msg.includes('failed to fetch') ||
          msg.includes('enoent') ||
          msg.includes('404') ||
          msg.includes('net::err_file_not_found')
        ) {
          // Silently handle file access errors - this is expected behavior
          setPreviewUnavailable(true);
          setFileAccessError(true);
          setLoading(false);
          setReady(false);
          return;
        }

        // Check if this is a video file that WaveSurfer can't handle
        if (msg.includes('decodeaudiodata') && url && typeof url === 'string') {
          const isVideoFile = [
            // Common video formats
            '.mp4',
            '.mov',
            '.avi',
            '.wmv',
            '.mkv',
            '.flv',
            '.webm',
            // Additional DJ/VJ formats
            '.m4v',
            '.mpg',
            '.mpeg',
            '.3gp',
            '.ogv',
            '.f4v',
            '.vob',
            '.ts',
            '.mts',
            '.m2ts',
            '.asf',
          ].some((ext) => url.toLowerCase().includes(ext));
          if (isVideoFile) {
            console.log(
              'Video file detected - WaveSurfer cannot process video files. Consider using MediaPlayer component instead.',
            );
          }
        }

        // Only log non-file-access errors for debugging
        console.error('WaveSurfer error (non-file-access):', err);
        setPreviewUnavailable(true);
        setLoading(false);
        setReady(false);
      });

      wavesurfer.current.on('finish', () => {
        dispatch(setPlaying(false));
        if (autoplay && changeTrack) changeTrack('next');
      });
    };

    /**
     * Check if a file exists before trying to load it
     */
    const checkFileExists = async (filePath: string): Promise<boolean> => {
      try {
        // For file:// URLs, we can't easily check existence from the browser
        // So we'll rely on the error handling in WaveSurfer
        return true;
      } catch (error) {
        return false;
      }
    };

    /**
     * Load peaks / audio once.
     */
    const loadAudio = async (autoPlay: boolean) => {
      ensureWaveSurfer();
      if (!wavesurfer.current || previewUnavailable) return;

      // Lazy‑resolve URL on play
      if (!url && loadUrl && autoPlay) {
        try {
          const fetched = await loadUrl();
          setUrl(fetched);
        } catch (e: any) {
          console.error('loadUrl failed', e);

          // Check if this is an LNRP preview error (404 or 502)
          const statusCode = e?.response?.status || e?.status;
          if (statusCode === 404 || statusCode === 502) {
            errorToast({
              message:
                'Sorry. We are having troubles connecting to LNRP for this track. We have been notified. Please click on the track to go directly to the website',
              duration: 5000,
            });
          }

          setPreviewUnavailable(true);
          return;
        }
      }
      if (!url) return;

      const start = () => autoPlay && wavesurfer.current?.play();

      if (!ready && !loading) {
        setLoading(true);
        if (url && typeof url == 'string') {
          if (isProtectedAudio(url)) {
            // Explicitly mark as not previewable for DRM formats
            setPreviewUnavailable(true);
            setLoading(false);
            setReady(false);
            return;
          }
          try {
            // Temporarily suppress console errors for file access issues
            const originalConsoleError = console.error;
            const originalConsoleWarn = console.warn;

            // Override console methods to filter out file access errors
            console.error = (...args: any[]) => {
              const message = args.join(' ');
              if (
                message.includes('Failed to fetch') ||
                message.includes('net::ERR_FILE_NOT_FOUND') ||
                message.includes('ENOENT') ||
                message.includes('404')
              ) {
                // Silently ignore file access errors
                return;
              }
              originalConsoleError.apply(console, args);
            };

            console.warn = (...args: any[]) => {
              const message = args.join(' ');
              if (
                message.includes('Failed to fetch') ||
                message.includes('net::ERR_FILE_NOT_FOUND') ||
                message.includes('ENOENT') ||
                message.includes('404')
              ) {
                // Silently ignore file access warnings
                return;
              }
              originalConsoleWarn.apply(console, args);
            };

            try {
              await wavesurfer.current.load(toPlayableUrl(url));
            } finally {
              // Restore original console methods
              console.error = originalConsoleError;
              console.warn = originalConsoleWarn;
            }
          } catch (error: any) {
            // Ignore aborts from rapid mount/unmount transitions
            if (
              error?.name === 'AbortError' ||
              (error?.message || '').toLowerCase().includes('signal is aborted')
            ) {
              setLoading(false);
              setReady(false);
              return;
            }
            // Check if this is a file access error (file was deleted or inaccessible)
            if (
              error.message?.includes('Failed to fetch') ||
              error.message?.includes('ENOENT') ||
              error.message?.includes('404') ||
              error.message?.includes('net::ERR_FILE_NOT_FOUND')
            ) {
              // Silently handle file access errors - this is expected behavior
              setPreviewUnavailable(true);
              setFileAccessError(true);
              setLoading(false);
              setReady(false);
              return;
            }

            // Only log non-file-access errors for debugging
            console.error('WaveSurfer load error (non-file-access):', error);
            setPreviewUnavailable(true);
            setLoading(false);
            setReady(false);
            return;
          }
        }
        wavesurfer.current.once('ready', start);
      } else if (!ready && loading) {
        wavesurfer.current.once('ready', start);
      } else if (ready && autoPlay) {
        wavesurfer.current.play();
      }
    };

    /**************************************
     *  Cleanup on file deletion (only if active and has URL)
     **************************************/
    useEffect(() => {
      // Only set up listeners for active or ready tracks with URLs
      if (!url || (!isActive && !ready)) return;

      const handleFileDeleted = (data: any) => {
        if (data.changeType === 'delete' && data.filePath === url) {
          console.log('🧹 File deleted, cleaning up WaveformInline:', url);
          setPreviewUnavailable(true);
          setLoading(false);
          setReady(false);

          // Pause but don't destroy WaveSurfer instance
          if (wavesurfer.current) {
            try {
              wavesurfer.current.pause();
            } catch (error) {
              console.warn('Error pausing WaveSurfer instance:', error);
            }
          }

          // Clear from global state if this was the active track
          if (isActive) {
            dispatch(setPlaying(false));
            dispatch(setURL(''));
          }
        }
      };

      // Listen for file change events
      on('music-folder-file-changed', handleFileDeleted);

      return () => {
        removeListener('music-folder-file-changed', handleFileDeleted);
      };
    }, [url, isActive, ready, dispatch]); // Added 'ready' to dependencies

    /**************************************
     *  Visibility‑triggered preview
     **************************************/
    useEffect(() => {
      if (!url || previewUnavailable) return;
      // Only auto-load when this instance is active and global is playing
      if (isActive && globalPlaying && !ready && !loading) loadAudio(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, previewUnavailable, isActive, globalPlaying, ready, loading]);

    /**************************************
     *  Reset file access error when URL changes
     **************************************/
    useEffect(() => {
      setFileAccessError(false);
    }, [url]);

    /**************************************
     *  Play / Pause
     **************************************/
    const togglePlay = async () => {
      if (isProtectedAudio(url)) {
        errorToast({
          message:
            'DRM-protected file. Click to reveal in Finder/Explorer and open in your default player.',
        });
        try {
          // Reveal in Finder/Explorer and let user open
          if (typeof window !== 'undefined' && (window as any).api?.send) {
            (window as any).api.send('open-file-in-explorer', {
              filePath: url,
            });
          }
        } catch {}
        return; // Do not attempt inline playback
      }
      if (isActive && globalPlaying) {
        dispatch(setPlaying(false));
        return;
      }
      // Remove drive-connection gating; attempt playback and surface a small inline notice if file is inaccessible
      if (redacted) {
        dispatch(showUpgradeModal());
        return;
      }
      // Publish the URL before loading so StickyPlayer drops any video preview.
      dispatch(setURL(url ?? ''));
      dispatch(setActiveInstance(instanceId));
      dispatch(setPlaying(true));
      await loadAudio(true);
    };

    /**************************************
     *  Imperative API
     **************************************/
    useImperativeHandle(ref, () => ({
      play: async () => {
        dispatch(setURL(url ?? ''));
        dispatch(setActiveInstance(instanceId));
        dispatch(setPlaying(true));
        await loadAudio(true);
      },
      moveToStart: () => ready && wavesurfer.current?.seekTo(0),
      moveToEnd: () => ready && wavesurfer.current?.seekTo(1),
    }));

    /**************************************
     *  Sync with global play flag
     **************************************/
    useEffect(() => {
      if (!ready || !wavesurfer.current) return;
      if (isActive && globalPlaying) wavesurfer.current.play();
      else wavesurfer.current.pause();
    }, [globalPlaying, isActive, ready, url]); // Added url to dependencies to prevent stale closures

    /**************************************
     *  Unload waveform when not active/playing to free memory
     **************************************/
    useEffect(() => {
      const ws = wavesurfer.current;
      if (!ws) return;
      // Avoid destroying while loading to prevent abort races
      if ((!isActive || !globalPlaying) && !loading) {
        try {
          ws.destroy();
        } catch {}
        wavesurfer.current = null;
        setReady(false);
        setLoading(false);
      }
    }, [isActive, globalPlaying, loading]);

    /**************************************
     *  Manage persistent player visibility for content spacing
     **************************************/
    useEffect(() => {
      // Only the active audio row may toggle the sticky bar. Inactive rows must
      // not call setPersistentPlayerVisible(false), or they hide video previews
      // started by MediaPlayer on match rows (Freshdesk #86052).
      if (!isActive) return;
      dispatch(setPersistentPlayerVisible(globalPlaying));
    }, [isActive, globalPlaying, dispatch]);

    /**************************************
     *  Render
     **************************************/
    return (
      <>
        <IonIcon
          icon={isActive && globalPlaying ? pause : play}
          onClick={togglePlay}
          title={
            isProtectedAudio(url)
              ? 'DRM-protected (no inline preview)'
              : undefined
          }
          style={{
            color: isProtectedAudio(url)
              ? 'var(--ion-color-medium)'
              : 'var(--ion-color-primary)',
            cursor: isProtectedAudio(url) ? 'not-allowed' : 'pointer',
            opacity: isProtectedAudio(url) ? 0.5 : 1,
            fontSize: `${height}px !important`,
            width: `${height}px !important`,
            height: `${height}px !important`,
            minWidth: `${height}px`,
            minHeight: `${height}px`,
          }}
        />
        {/* Keep UI compact; parent page can show badges/messages if needed */}
        {/* 
      This is used to create a portal to outside the cell, 
      without this the sticky player will be restricted to the AGrid cell
      */}
        {ReactDOM.createPortal(
          <div ref={wrapperEl} onClick={(e) => e.stopPropagation()}>
            <div
              className={
                isActive && globalPlaying
                  ? 'persistent-player active'
                  : 'persistent-player hidden'
              }
            >
              {previewUnavailable ? (
                <div className="no-preview">
                  {fileAccessError
                    ? 'File not accessible'
                    : 'No preview available'}
                </div>
              ) : (
                <>
                  <div className="player-button" onClick={togglePlay}>
                    <IonIcon icon={isActive && globalPlaying ? pause : play} />
                  </div>

                  <div
                    ref={waveformEl}
                    className={`waveform ${loading ? 'loading' : ''}`}
                    style={{ height }}
                  >
                    {!ready && !previewUnavailable && (
                      <div className="flat-line" />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
      </>
    );
  },
);

WaveformInline.displayName = 'WaveformInline';

export default WaveformInline;
